/**
 * Fullscreen overlay for element inspection
 * Tracks hover and captures clicks
 */

import { useState, useEffect, useRef } from 'react';
import {
  extractElementMetadata,
  isSystemElement,
  type ElementMetadata,
} from '../../services/devfix/elementInspector';

interface Props {
  onElementSelected: (metadata: ElementMetadata) => void;
  onCancel: () => void;
}

export function ElementInspectorOverlay({ onElementSelected, onCancel }: Props) {
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);
  const [highlightBox, setHighlightBox] = useState<DOMRect | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!overlayRef.current) return;

      // Temporarily disable overlay pointer events to find element underneath
      overlayRef.current.style.pointerEvents = 'none';
      const element = document.elementFromPoint(e.clientX, e.clientY);
      overlayRef.current.style.pointerEvents = 'auto';

      // Ignore system elements (overlay, toolbar, etc.)
      if (!element || isSystemElement(element)) {
        setHighlightedElement(null);
        setHighlightBox(null);
        return;
      }

      setHighlightedElement(element);
      setHighlightBox(element.getBoundingClientRect());
    };

    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (highlightedElement) {
        const metadata = extractElementMetadata(highlightedElement);
        console.log('Element selected:', metadata);
        onElementSelected(metadata);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick, true); // Capture phase
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [highlightedElement, onElementSelected, onCancel]);

  return (
    <>
      {/* Fullscreen overlay */}
      <div
        ref={overlayRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          cursor: 'crosshair',
          background: 'rgba(0, 0, 0, 0.05)',
        }}
      />

      {/* Element highlight */}
      {highlightBox && (
        <div
          style={{
            position: 'fixed',
            top: `${highlightBox.top}px`,
            left: `${highlightBox.left}px`,
            width: `${highlightBox.width}px`,
            height: `${highlightBox.height}px`,
            border: '2px solid #3b82f6',
            background: 'rgba(59, 130, 246, 0.1)',
            pointerEvents: 'none',
            zIndex: 100000,
            transition: 'all 0.1s ease-out',
          }}
        />
      )}

      {/* Instructions */}
      <div
        style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 24px',
          background: 'white',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 100001,
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          color: '#1f2937',
        }}
      >
        Click element to inspect • <kbd>ESC</kbd> to cancel
      </div>
    </>
  );
}
